// Lecture d'un fichier Excel de dépenses (situation des charges). Reconnaît le format habituel :
//   une ligne de titre « DEPENSES du … », une ligne d'en-tête DATE | LIBELLE | MONTANT, les lignes, une ligne TOTAL (ignorée).
// Colonnes supplémentaires acceptées si présentes : CATEGORIE, FOURNISSEUR, PIECE / N° REÇU, MODE (de paiement),
//   QUANTITE, PRIX / PU, UNITE, OBSERVATION. Un onglet par mois est géré (chaque feuille sélectionnée est analysée).
import { enDate, norme } from './excel';

const MODES = { especes: 'ESPECES', espece: 'ESPECES', cash: 'ESPECES', cheque: 'CHEQUE', virement: 'VIREMENT', wave: 'WAVE', 'orange money': 'ORANGE_MONEY', 'orange-money': 'ORANGE_MONEY', om: 'ORANGE_MONEY', 'mobile money': 'MOBILE_MONEY', momo: 'MOBILE_MONEY', autre: 'AUTRE' };
const nombre = (v) => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return v;
  const s = String(v).replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};
// Repère la colonne par mots-clés dans l'en-tête
const COLS = [
  ['date', ['date']],
  ['libelle', ['libelle', 'designation', 'objet', 'nature', 'intitule', 'description', 'charge']],
  ['montant', ['montant', 'total', 'somme', 'prix total', 'valeur', 'depense']],
  ['categorie', ['categorie', 'rubrique', 'type', 'poste']],
  ['fournisseur', ['fournisseur', 'beneficiaire', 'prestataire', 'chez']],
  ['piece', ['piece', 'recu', 'facture', 'reference', 'ref', 'n°', 'numero']],
  ['mode_paiement', ['mode', 'paiement', 'reglement']],
  ['quantite', ['quantite', 'qte', 'quantité']],
  ['prix_unitaire', ['prix unitaire', 'pu', 'prix u']],
  ['unite', ['unite', 'unité', 'u']],
  ['observation', ['observation', 'commentaire', 'remarque', 'note']],
];
function trouverEntete(lignes) {
  for (let i = 0; i < Math.min(lignes.length, 15); i += 1) {
    const r = lignes[i].map((c) => norme(c));
    const map = {};
    COLS.forEach(([cle, mots]) => {
      const j = r.findIndex((c) => c && mots.some((m) => c === m || c.startsWith(`${m} `) || c.includes(m)));
      if (j >= 0 && map[cle] === undefined) map[cle] = j;
    });
    // Une feuille de dépenses a au minimum une colonne DATE et une colonne MONTANT (le libellé se déduit sinon)
    if (map.montant !== undefined && (map.date !== undefined || map.libelle !== undefined)) return { i, map };
  }
  return null;
}

/**
 * analyser(feuilles) → objet attendu par ImportDialog.
 * Chaque feuille peut porter un mois différent (un onglet = un mois).
 */
export function analyserDepenses(feuilles) {
  const colonnes = ['Date', 'Libellé', 'Montant', 'Catégorie', 'Fournisseur', 'N° pièce', 'Mode'];
  const apercu = []; const erreurs = []; const lignesOut = [];
  let feuillesReconnues = 0;
  for (const f of feuilles) {
    const ent = trouverEntete(f.lignes);
    if (!ent) { erreurs.push(`Onglet « ${f.feuille} » : en-tête DATE / LIBELLÉ / MONTANT introuvable`); continue; }
    feuillesReconnues += 1;
    const { i, map } = ent;
    const cell = (r, cle) => (map[cle] !== undefined ? r[map[cle]] : null);
    for (let k = i + 1; k < f.lignes.length; k += 1) {
      const r = f.lignes[k];
      if (!r || r.every((c) => c === null || c === undefined || String(c).trim() === '')) continue;
      const libelleBrut = cell(r, 'libelle');
      const lib = libelleBrut === null || libelleBrut === undefined ? '' : String(libelleBrut).trim();
      // Ligne de total : pas de date, libellé « total »
      if (/^total/i.test(norme(lib)) && !enDate(cell(r, 'date'))) continue;
      const montant = nombre(cell(r, 'montant'));
      const date = enDate(cell(r, 'date'));
      if (montant === null && !lib) continue; // ligne vide ou décorative
      const numLigne = `Onglet « ${f.feuille} », ligne ${k + 1}`;
      if (!lib) { erreurs.push(`${numLigne} : libellé manquant`); continue; }
      if (montant === null) { erreurs.push(`${numLigne} : montant illisible (« ${cell(r, 'montant') ?? ''} »)`); continue; }
      if (!date) { erreurs.push(`${numLigne} : date manquante ou illisible`); continue; }
      const q = nombre(cell(r, 'quantite')); const pu = nombre(cell(r, 'prix_unitaire'));
      const mode = MODES[norme(cell(r, 'mode_paiement'))] || null;
      const ligne = {
        date_depense: date, libelle: lib.slice(0, 255), montant: Math.round(montant * 100) / 100,
        categorie: cell(r, 'categorie') ? String(cell(r, 'categorie')).trim().slice(0, 40) : null,
        fournisseur: cell(r, 'fournisseur') ? String(cell(r, 'fournisseur')).trim().slice(0, 150) : null,
        piece: cell(r, 'piece') ? String(cell(r, 'piece')).trim().slice(0, 60) : null,
        mode_paiement: mode, quantite: q, prix_unitaire: pu,
        unite: cell(r, 'unite') ? String(cell(r, 'unite')).trim().slice(0, 20) : null,
        observation: cell(r, 'observation') ? String(cell(r, 'observation')).trim().slice(0, 255) : null,
      };
      lignesOut.push(ligne);
      if (apercu.length < 200) apercu.push([date.split('-').reverse().join('/'), lib, Math.round(montant).toLocaleString('fr-FR'), ligne.categorie || '', ligne.fournisseur || '', ligne.piece || '', mode || '']);
    }
  }
  const total = lignesOut.reduce((s, l) => s + l.montant, 0);
  const resume = lignesOut.length
    ? `${lignesOut.length} dépense(s) reconnue(s)${feuillesReconnues > 1 ? ` sur ${feuillesReconnues} onglets` : ''} · total ${Math.round(total).toLocaleString('fr-FR')} F`
    : 'Aucune dépense reconnue';
  return { colonnes, apercu, erreurs, resume, payload: lignesOut.length ? { lignes: lignesOut } : null };
}
